import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { ApprovalStats } from '../types';
import { approvalKeys } from '../types';

// Database record types
type DatabaseRecord = Record<string, unknown>;
type FilterValue = string | number | boolean | null;
type SafeTableQueryResult = { count: number; exists: boolean; error: string | null };
type SafeDataQueryResult = { data: DatabaseRecord[]; exists: boolean; error: string | null };

// Utility to safely query a table with error handling
const safeTableQuery = async (
  tableName: string,
  query: string = 'id',
  filters: Record<string, FilterValue> = {}
): Promise<SafeTableQueryResult> => {
  try {
    // Use GET + limit(0) instead of HEAD to avoid HEAD failures in some environments
    let queryBuilder = supabase.from(tableName).select(query, { count: 'exact' }).limit(0);
    
    // Apply filters
    Object.entries(filters).forEach(([key, value]) => {
      queryBuilder = queryBuilder.eq(key, value);
    });
    
    const result = await queryBuilder;
    
    if (result.error) {
      console.warn(`Error querying ${tableName}:`, result.error.message);
      return { count: 0, exists: false, error: result.error.message };
    }
    
    return { count: result.count || 0, exists: true, error: null };
  } catch (e) {
    console.warn(`Failed to query ${tableName}:`, e);
    return { count: 0, exists: false, error: String(e) };
  }
};

// Utility to safely query table data with error handling
const safeDataQuery = async (
  tableName: string,
  query: string,
  filters: Record<string, FilterValue> = {}
): Promise<SafeDataQueryResult> => {
  try {
    let queryBuilder = supabase.from(tableName).select(query);
    
    // Apply filters
    Object.entries(filters).forEach(([key, value]) => {
      queryBuilder = queryBuilder.eq(key, value);
    });
    
    const result = await queryBuilder;
    
    if (result.error) {
      console.warn(`Error querying ${tableName} data:`, result.error.message);
      return { data: [], exists: false, error: result.error.message };
    }

    const data = Array.isArray(result.data) ? (result.data as unknown as DatabaseRecord[]) : [];
    return { data, exists: true, error: null };
  } catch (e) {
    console.warn(`Failed to query ${tableName} data:`, e);
    return { data: [], exists: false, error: String(e) };
  }
};

// Fetch approval statistics
const fetchApprovalStats = async (): Promise<ApprovalStats> => {
  console.log('🔍 Fetching approval statistics...');

  // Check which tables exist and get counts
  const [galleriesCount, imagesCount] = await Promise.all([
    safeTableQuery('galleries', '*', { review_status: 'pending' }),
    safeTableQuery('franchise_image_jobs', '*', { review_status: 'pending' })
  ]);

  // Check approved/rejected counts
  const [galleriesApproved, galleriesRejected, imagesApproved, imagesRejected] = await Promise.all([
    safeTableQuery('galleries', '*', { review_status: 'approved' }),
    safeTableQuery('galleries', '*', { review_status: 'rejected' }),
    safeTableQuery('franchise_image_jobs', '*', { review_status: 'approved' }),
    safeTableQuery('franchise_image_jobs', '*', { review_status: 'rejected' })
  ]);

  // Get region and service breakdowns from existing tables only
  const [galleriesData, imagesData] = await Promise.all([
    galleriesCount.exists
      ? safeDataQuery('galleries', 'franchise_region, primary_service', { review_status: 'pending' })
      : { data: [], exists: false, error: galleriesCount.error || 'Table not available' },
    imagesCount.exists
      ? safeDataQuery('franchise_image_jobs', 'franchise_region', { review_status: 'pending' })
      : { data: [], exists: false, error: imagesCount.error || 'Table not available' }
  ]);

  // Calculate region breakdown
  const by_region: Record<string, number> = {};
  const allData = [
    ...(Array.isArray(galleriesData.data) ? galleriesData.data : []),
    ...(Array.isArray(imagesData.data) ? imagesData.data : [])
  ];
  allData.forEach((item: DatabaseRecord) => {
    const region = typeof item['franchise_region'] === 'string' ? (item['franchise_region'] as string) : 'general';
    by_region[region] = (by_region[region] || 0) + 1;
  });

  // Calculate service breakdown
  const by_service: Record<string, number> = {};
  const galleryData = Array.isArray(galleriesData.data) ? galleriesData.data : [];
  galleryData.forEach((gallery: DatabaseRecord) => {
    const svc = gallery['primary_service'];
    if (typeof svc === 'string') {
      by_service[svc] = (by_service[svc] || 0) + 1;
    }
  });

  const pending_galleries = galleriesCount.count || 0;
  const pending_images = imagesCount.count || 0;
  
  const stats = {
    total_pending: pending_galleries + pending_images,
    pending_galleries,
    pending_images,
    approved_today: (galleriesApproved.count || 0) + (imagesApproved.count || 0),
    rejected_today: (galleriesRejected.count || 0) + (imagesRejected.count || 0),
    by_region,
    by_service
  };

  console.log('📊 Approval stats:', {
    ...stats,
    tableStatus: {
      galleries: galleriesCount.exists ? 'exists' : `missing (${galleriesCount.error})`,
      franchise_image_jobs: imagesCount.exists ? 'exists' : `missing (${imagesCount.error})`
    }
  });
  
  return stats;
};

export const useApprovalStats = () => {
  return useQuery({
    queryKey: approvalKeys.stats(),
    queryFn: fetchApprovalStats,
    refetchInterval: 60000, // Refresh every minute
    staleTime: 30000 // Consider data stale after 30 seconds
  });
};
